/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import React, { forwardRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { config } from '_config';

import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { ICONS_ALIASES } from '_constants/icons';

import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledBottomBannerSection = styled(
  forwardRef(({ moreSpace, ...props }, ref) => <div ref={ref} {...props} />)
)`
  display: flex;
  justify-content: space-between;
  background: ${colors.indigo[600]} no-repeat;
  background-position: 100% 100%;
  background-image: ${`url(${config.basePath}/images/bottom-banner-rocket-llc.svg)`};
  border-radius: 40px;
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  padding-left: ${spaces.large4X}px;
  padding-right: ${spaces.large4X}px;
  padding-top: ${spaces.large4X}px;
  padding-bottom: ${spaces.large4X}px;

  @media ${devices.tablet} {
    margin-left: 0;
    margin-right: 0;
    border-radius: 0px;
    background: ${colors.indigo[600]};
    padding-left: ${spaces.regular}px;
    padding-right: ${spaces.regular}px;
    padding-top: ${spaces.large}px;
    padding-bottom: ${spaces.large}px;
    align-items: center;
    gap: 24px;
    flex-direction: column;
  }
`;

const StyledDesktopButton = styled.div`
  display: inline;

  @media ${devices.tablet} {
    display: none;
  }
`;

const StyledMobileButton = styled.div`
  display: none;

  @media ${devices.tablet} {
    display: inline;
    width: 100%;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const StyledColumn1 = styled.div`
  color: white;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  padding-left: ${spaces.large4X}px;
  padding-right: ${spaces.large4X}px;
  padding-top: ${spaces.large4X}px;
  padding-bottom: ${spaces.large4X}px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  font-size: ${fontSizes.medium}px;
  align-items: start;

  .header {
    font-style: normal;
    font-weight: 500;
    font-size: ${fontSizes.large11X}px;
    line-height: 130%;
    color: ${colors.white};
    align-self: stretch;
  }

  @media ${devices.tablet} {
    padding: 0px;
    gap: 0px;

    .header {
      font-size: ${fontSizes.large2X}px;
      margin-bottom: ${spaces.regular}px;
    }

    p {
      font-size: ${fontSizes.regular}px;
      font-weight: 400;
      margin-bottom: 0px;
    }

    .footer {
      align-items: flex-start;
      gap: 10px;
    }
  }
`;

const StyledColumn2 = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.gray[200]};
  max-width: 420px;
  border-radius: 30px;
  background: white;
  align-items: center;
  padding: ${spaces.medium}px;
  gap: 8px;

  @media ${devices.tablet} {
    padding: ${spaces.regular}px;
  }
`;

const StyledGold = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 4px 14px;
  gap: 10px;
  background: #ffbf00;
  border-radius: 100px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.regular}px;
  line-height: 150%;
  color: #ffffff;
  margin-bottom: -1.5em;
  z-index: 1;
`;

const StyledColumnYellow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 30px 10px 20px;
  gap: 16px;
  width: 100%;
  background: #fffae5;
  border-radius: 20px;
  margin-bottom: 12px;
`;

const StyledColumnYellowCol1 = styled.div`
  display: flex;

  @media ${devices.tablet} {
    img {
      width: 100px;
    }
  }
`;

const StyledColumnYellowCol2 = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledPrice = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 700;
  font-size: ${fontSizes.large9X}px;
  line-height: 48px;
  /* gray/800 */
  color: #1d2939;
  @media ${devices.tablet} {
    font-size: ${fontSizes.large8X}px;
  }
`;

const StyledFee = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.regular}px;
  line-height: 150%;
  color: ${colors.gray[700]};
`;

const StyledBare = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 700;
  font-size: ${fontSizes.regular}px;
  line-height: 150%;
  text-transform: uppercase;
  color: ${colors.gray[800]};
  margin-top: 10px;
  margin-bottom: 4px;

  @media ${devices.tablet} {
    font-size: ${fontSizes.small}px;
    margin-bottom: 0px;
  }
`;

const StyledDescription = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.regular}px;
  line-height: 150%;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    font-size: ${fontSizes.small}px;
  }
`;

const StyledItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
`;

const StyledItemLeft = styled.div``;

const StyledItemRight = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.regular}px;
  line-height: 150%;
  color: ${colors.gray[700]};
`;

const items = [
  { title: 'Prepare & Filing Incorporation Paperwork' },
  { title: 'Banking Resolution' },
  { title: 'Operating Agreement' },
  { title: '24/7 Customer Support' },
];

const BottomBannerLLCSection = ({ price, moreSpace }) => {
  const router = useRouter();

  const handleStartRegisteringLLC = useCallback(() => {
    router.push(ROUTES.llcIncorporationFiling);
  }, [router]);

  return (
    <StyledBottomBannerSection moreSpace={moreSpace}>
      <StyledColumn1>
        <div className="header">Start Forming Your LLC Today</div>
        <p>
          &quot;Take the first step towards protecting yourself and your assets
          by incorporating your business today.&quot;
        </p>
        <StyledDesktopButton>
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={colors.indigo[600]}
              />
            }
            color={colors.white}
            textColor={colors.indigo[600]}
            hoverColor={colors.white}
            hoverTextColor={colors.indigo[600]}
            onClick={handleStartRegisteringLLC}
          >
            Start Registering Your LLC Now
          </Button>
        </StyledDesktopButton>
      </StyledColumn1>
      <StyledColumn2>
        <StyledGold>GOLD</StyledGold>
        <StyledColumnYellow>
          <StyledColumnYellowCol1>
            <picture>
              <source
                srcSet={`${config.basePath}/images/character.svg`}
                type="image/png"
              />
              <img
                src={`${config.basePath}/images/character.svg`}
                title="Animated Character Starting Business"
                alt="Animated Character Starting Business"
              />
            </picture>
          </StyledColumnYellowCol1>
          <StyledColumnYellowCol2>
            <StyledPrice>${price}</StyledPrice>
            <StyledFee>+State fee</StyledFee>
            <StyledBare>Bare Essentials</StyledBare>
            <StyledDescription>
              You will Be Required to Keep Up with Your LLC Filings
            </StyledDescription>
          </StyledColumnYellowCol2>
        </StyledColumnYellow>
        {items.map(({ title }, idx) => (
          <StyledItemContainer key={idx}>
            <StyledItemLeft>
              <Icon alias={ICONS_ALIASES.tickCircle} color="#FFBF00" />
            </StyledItemLeft>
            <StyledItemRight>{title}</StyledItemRight>
          </StyledItemContainer>
        ))}
      </StyledColumn2>
      <StyledMobileButton>
        <Button
          suffix={
            <Icon alias={ICONS_ALIASES.arrowRight} color={colors.indigo[600]} />
          }
          color={colors.white}
          textColor={colors.indigo[600]}
          hoverColor={colors.white}
          hoverTextColor={colors.indigo[600]}
          onClick={handleStartRegisteringLLC}
        >
          Start Registering Your LLC Now
        </Button>
      </StyledMobileButton>
    </StyledBottomBannerSection>
  );
};

BottomBannerLLCSection.propTypes = {
  price: PropTypes.number,
  moreSpace: PropTypes.bool,
};

export default BottomBannerLLCSection;
